/**
 * Scope geometry version — raster tile cache-buster (AK-587).
 *
 * Why this exists
 * ---------------
 * A raster tile URL used to be a pure function of (product, date, farm_id).
 * Editing a field contour changes what the tile *should* look like without
 * changing a single byte of that URL, so the stale PNG survived in all three
 * caches: nginx (`proxy_cache_valid 200 30d`, key `$scheme$request_uri$http_cookie`),
 * the browser (`Cache-Control: max-age=86400`) and MapLibre's in-memory tile
 * map (which survives a plain `setTiles()` — CLAUDE.md «Регрессии MVT-фильтра»).
 *
 * The backend exposes `GET /api/v1/ndvi/scope-version` — a short token derived
 * from `COUNT(*)` + `MAX(updated_at)` of the scope's fields. We append it to
 * raster URLs as `_v=…` (the MVT source has done this for a long time; this is
 * the symmetric raster half). Because `_v` lives in the request URI it busts
 * nginx and the browser too, not just MapLibre.
 *
 * Registry, not a store slice
 * ---------------------------
 * The version is consumed by several independent URL builders (map overlay,
 * tile prewarm, prefetch). Threading an async value through every call site
 * would mean touching the shared map store (Head-Web territory) and every
 * prewarm signature. Instead the fetch effect publishes into this tiny
 * module-local registry and the builders read it synchronously, with an
 * explicit override parameter kept for test determinism.
 */

export type ScopeVersionScopeType = string;

const versions = new Map<string, string>();

/** Registry key. Non-farm scopes share the `null` id slot — raster products
 *  are farm-only (see buildScopeRasterParam), so that slot is never used for
 *  tiles; it is kept so the fetch effect can cache a miss uniformly. */
export function scopeVersionKey(
  scopeType: ScopeVersionScopeType,
  scopeId: string | null,
): string {
  return `${scopeType}:${scopeId ?? ''}`;
}

export function getScopeGeomVersion(
  scopeType: ScopeVersionScopeType,
  scopeId: string | null,
): string | null {
  return versions.get(scopeVersionKey(scopeType, scopeId)) ?? null;
}

export function setScopeGeomVersion(
  scopeType: ScopeVersionScopeType,
  scopeId: string | null,
  version: string | null,
): void {
  const key = scopeVersionKey(scopeType, scopeId);
  if (version) versions.set(key, version);
  else versions.delete(key);
}

/** Test hook — drops every memoized version. */
export function clearScopeGeomVersions(): void {
  versions.clear();
  refreshListeners.clear();
}

// ── Refresh signal ───────────────────────────────────────────────────────────
//
// AK-587: the version must be re-read after the user *edits* geometry, not only
// when the scope changes. The agronomist saves a contour and stays on the same
// farm: without this the raster URL never changes, so nginx keeps serving the
// pre-edit PNG (`proxy_cache_valid 200 30d`, key on `$request_uri`) even though
// the backend would now compute a different key — nobody asks it. The vector
// half already solves this with `invalidateFieldsMvt` (AK-334); this is the
// raster half of the same signal.
//
// A module-local emitter rather than a store slice: it keeps the wiring inside
// the satellite vertical (one import in pages/map/v2.tsx) instead of adding a
// field to the shared map store.

type RefreshListener = () => void;

const refreshListeners = new Set<RefreshListener>();

/** Subscribe to «geometry changed, re-read the version». Returns unsubscribe. */
export function onScopeGeomVersionRefresh(fn: RefreshListener): () => void {
  refreshListeners.add(fn);
  return () => {
    refreshListeners.delete(fn);
  };
}

/**
 * Ask every live raster source to re-read its scope version.
 *
 * Called right where the vector layer invalidates itself after a field save.
 * Listener errors are contained — a broken subscriber must not break the save
 * flow that triggered this.
 */
export function requestScopeGeomVersionRefresh(): void {
  for (const fn of [...refreshListeners]) {
    try {
      fn();
    } catch {
      // Best-effort notification; a failed refresh only costs freshness.
    }
  }
}

interface ScopeVersionResponse {
  version?: string;
}

/**
 * Fetch the current geometry version for a scope.
 *
 * Best-effort: a failed request resolves to `null` (URLs then simply carry no
 * `_v`, i.e. pre-AK-587 behaviour). Correctness does not depend on this —
 * the backend folds the same token into its own disk-cache key server-side —
 * so a transient failure costs freshness at the edge, never a broken map.
 */
export async function fetchScopeGeomVersion(
  scopeType: ScopeVersionScopeType,
  scopeId: string | null,
  signal?: AbortSignal,
): Promise<string | null> {
  const qs = scopeType === 'farm' && scopeId ? `?farm_id=${encodeURIComponent(scopeId)}` : '';
  const res = await fetch(`/api/v1/ndvi/scope-version${qs}`, {
    signal,
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`scope-version ${res.status}`);
  const body = (await res.json()) as ScopeVersionResponse;
  return body.version ?? null;
}
